import { cents } from '../money.js';
import { CATEGORIES, type Category, type Item, type Receipt } from '../types.js';
import type { ItemSelector, PersonSet, SplitOp } from '../split/dsl.js';
import type { DeclaredPerson, SplitReply } from '../split/reply.js';

/**
 * Hand-written parsers for model output.
 *
 * No schema library on purpose: `packages/core` stays dependency-free so it can
 * be imported by the app, by the eval harness and by a serverless function
 * without dragging a runtime along. These parsers are the boundary where
 * "whatever the model said" becomes typed data — nothing downstream re-checks
 * shape, so everything must be rejected here.
 */

export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; errors: string[] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isCategory = (value: unknown): value is Category =>
  typeof value === 'string' && (CATEGORIES as readonly string[]).includes(value);

/** Optional integer field: absent and null are both "not present". */
function optionalInt(
  source: Record<string, unknown>,
  key: string,
  errors: string[],
): number | undefined {
  const raw = source[key];
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw !== 'number' || !Number.isInteger(raw)) {
    errors.push(`${key} must be an integer number of cents`);
    return undefined;
  }
  return raw;
}

export function parseReceiptDraft(raw: unknown): ParseResult<Receipt> {
  const errors: string[] = [];
  if (!isRecord(raw)) return { ok: false, errors: ['response is not an object'] };

  let currency = 'XXX';
  if (typeof raw['currency'] === 'string' && raw['currency'].trim() !== '') {
    currency = raw['currency'].trim().toUpperCase();
  } else {
    errors.push('currency is missing');
  }

  const merchant = typeof raw['merchant'] === 'string' ? raw['merchant'].trim() : undefined;

  const rawItems = raw['items'];
  const items: Item[] = [];
  if (!Array.isArray(rawItems)) {
    errors.push('items must be an array');
  } else {
    const seen = new Set<string>();
    rawItems.forEach((entry, i) => {
      if (!isRecord(entry)) {
        errors.push(`items[${i}] is not an object`);
        return;
      }
      // Count errors raised by *this* item only — a bad item earlier in the
      // list must not silently swallow the valid ones after it.
      const before = errors.length;
      const id = typeof entry['id'] === 'string' ? entry['id'] : '';
      const name = typeof entry['name'] === 'string' ? entry['name'].trim() : '';
      const qty = entry['qty'];
      const unitPrice = entry['unitPriceCents'];
      const lineTotal = entry['lineTotalCents'];
      const category = entry['category'];

      if (id === '') errors.push(`items[${i}].id is missing`);
      else if (seen.has(id)) errors.push(`items[${i}].id "${id}" is duplicated`);
      seen.add(id);

      if (name === '') errors.push(`items[${i}].name is missing`);
      if (typeof qty !== 'number' || !Number.isInteger(qty) || qty < 1) {
        errors.push(`items[${i}].qty must be a positive integer`);
      }
      if (typeof unitPrice !== 'number' || !Number.isInteger(unitPrice)) {
        errors.push(`items[${i}].unitPriceCents must be an integer`);
      }
      if (typeof lineTotal !== 'number' || !Number.isInteger(lineTotal)) {
        errors.push(`items[${i}].lineTotalCents must be an integer`);
      }
      if (!isCategory(category)) {
        errors.push(`items[${i}].category must be one of ${CATEGORIES.join(', ')}`);
      }

      if (errors.length > before) return;
      items.push({
        id,
        name,
        qty: qty as number,
        unitPrice: cents(unitPrice as number),
        lineTotal: cents(lineTotal as number),
        category: category as Category,
      });
    });
  }

  const subtotal = optionalInt(raw, 'subtotalCents', errors);
  const tax = optionalInt(raw, 'taxCents', errors);
  const tip = optionalInt(raw, 'tipCents', errors);
  const total = optionalInt(raw, 'totalCents', errors);

  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    value: {
      currency,
      ...(merchant !== undefined && merchant !== '' ? { merchant } : {}),
      items,
      ...(subtotal !== undefined ? { subtotal: cents(subtotal) } : {}),
      ...(tax !== undefined ? { tax: cents(tax) } : {}),
      ...(tip !== undefined ? { tip: cents(tip) } : {}),
      ...(total !== undefined ? { total: cents(total) } : {}),
    },
  };
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string');

function parsePersonSet(raw: unknown, path: string, errors: string[]): PersonSet | null {
  if (!isRecord(raw)) {
    errors.push(`${path} is not an object`);
    return null;
  }
  const ids = raw['ids'];
  if (!isStringArray(ids)) {
    errors.push(`${path}.ids must be an array of strings`);
    return null;
  }
  if (raw['kind'] === 'only') return { kind: 'only', ids };
  if (raw['kind'] === 'allExcept') return { kind: 'allExcept', ids };
  errors.push(`${path}.kind must be "only" or "allExcept"`);
  return null;
}

function parseItemSelector(
  raw: unknown,
  path: string,
  errors: string[],
): ItemSelector | null {
  if (!isRecord(raw)) {
    errors.push(`${path} is not an object`);
    return null;
  }
  switch (raw['kind']) {
    case 'ids': {
      const ids = raw['ids'];
      if (!isStringArray(ids)) {
        errors.push(`${path}.ids must be an array of strings`);
        return null;
      }
      return { kind: 'ids', ids };
    }
    case 'category': {
      if (!isCategory(raw['category'])) {
        errors.push(`${path}.category must be one of ${CATEGORIES.join(', ')}`);
        return null;
      }
      return { kind: 'category', category: raw['category'] };
    }
    case 'rest':
      return { kind: 'rest' };
    default:
      errors.push(`${path}.kind must be "ids", "category" or "rest"`);
      return null;
  }
}

export function parseSplitOps(raw: unknown): ParseResult<SplitOp[]> {
  const errors: string[] = [];
  const list = isRecord(raw) && Array.isArray(raw['ops']) ? raw['ops'] : raw;
  if (!Array.isArray(list)) return { ok: false, errors: ['response is not an array of ops'] };

  const ops: SplitOp[] = [];
  list.forEach((entry, i) => {
    if (!isRecord(entry)) {
      errors.push(`ops[${i}] is not an object`);
      return;
    }
    const items = parseItemSelector(entry['items'], `ops[${i}].items`, errors);
    if (!items) return;

    if (entry['kind'] === 'assign') {
      const to = parsePersonSet(entry['to'], `ops[${i}].to`, errors);
      if (!to) return;
      const rawWeights = entry['weights'];
      if (rawWeights === undefined || rawWeights === null) {
        ops.push({ kind: 'assign', items, to });
        return;
      }
      if (!Array.isArray(rawWeights) || !rawWeights.every((w) => Number.isInteger(w))) {
        errors.push(`ops[${i}].weights must be an array of integers`);
        return;
      }
      ops.push({ kind: 'assign', items, to, weights: rawWeights as number[] });
      return;
    }

    if (entry['kind'] === 'unassign') {
      const from = parsePersonSet(entry['from'], `ops[${i}].from`, errors);
      if (!from) return;
      ops.push({ kind: 'unassign', items, from });
      return;
    }

    errors.push(`ops[${i}].kind must be "assign" or "unassign"`);
  });

  return errors.length > 0 ? { ok: false, errors } : { ok: true, value: ops };
}

/**
 * The full reply: ops plus the people the sentence introduced. A bare array or
 * a `{ops}` object (the pre-roster-less shape) still parses, with no people.
 */
export function parseSplitReply(raw: unknown): ParseResult<SplitReply> {
  const ops = parseSplitOps(raw);
  if (!ops.ok) return ops;
  const errors: string[] = [];
  const record = isRecord(raw) ? raw : {};

  const people: DeclaredPerson[] = [];
  const rawPeople = record['people'];
  if (rawPeople !== undefined && rawPeople !== null) {
    if (!Array.isArray(rawPeople)) {
      errors.push('people must be an array');
    } else {
      const seen = new Set<string>();
      rawPeople.forEach((entry, i) => {
        if (!isRecord(entry) || typeof entry['id'] !== 'string' || typeof entry['name'] !== 'string') {
          errors.push(`people[${i}] must have a string id and name`);
          return;
        }
        if (seen.has(entry['id'])) {
          errors.push(`people[${i}] repeats id "${entry['id']}"`);
          return;
        }
        seen.add(entry['id']);
        people.push({ id: entry['id'], name: entry['name'] });
      });
    }
  }

  let groupSize: number | undefined;
  const rawSize = record['groupSize'];
  if (rawSize !== undefined && rawSize !== null) {
    if (typeof rawSize !== 'number' || !Number.isInteger(rawSize) || rawSize < 1 || rawSize > 100) {
      errors.push('groupSize must be a whole number between 1 and 100');
    } else {
      groupSize = rawSize;
    }
  }

  const everyone = record['everyone'] === true;

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    value: { people, ...(groupSize !== undefined ? { groupSize } : {}), everyone, ops: ops.value },
  };
}
